import { useState, useEffect, useCallback } from 'react';
import { getInvoicesSummary } from '../api/endpoints/invoicesSummary';
import { useDebtorAccount } from '../utils/hooks/useDebtorAccount';
import { useCompany } from '../utils/hooks/useCompany';

export interface InvoiceSummaryCategory {
  totalAmount: string;
  count: string;
}

export interface InvoicesSummaryData {
  paymentsPastDue: InvoiceSummaryCategory;
  upcomingInvoices: InvoiceSummaryCategory;
  paymentsNotReceived: InvoiceSummaryCategory;
}

interface UseInvoicesSummaryReturn {
  invoiceSummary: InvoicesSummaryData;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const defaultSummaryData: InvoicesSummaryData = {
  paymentsPastDue: {
    totalAmount: '0',
    count: '0',
  },
  paymentsNotReceived: {
    totalAmount: '0',
    count: '0',
  },
  upcomingInvoices: {
    totalAmount: '0',
    count: '0',
  },
};

export interface UseInvoicesSummaryOptions {
  enabled?: boolean;
}

export const useInvoicesSummary = (
  options: UseInvoicesSummaryOptions = {},
): UseInvoicesSummaryReturn => {
  const { enabled = true } = options;
  const { selectedDebtor } = useDebtorAccount();
  const { selectedCompanyId, initialized: companyInitialized } = useCompany();

  const [invoiceSummary, setInvoiceSummary] =
    useState<InvoicesSummaryData>(defaultSummaryData);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchInvoicesSummary = useCallback(async (signal: AbortSignal) => {
    if (!enabled) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await getInvoicesSummary(signal);

      // Only update state if request wasn't aborted
      if (!signal.aborted) {
        setInvoiceSummary({
          paymentsPastDue:
            response?.paymentsPastDue ?? defaultSummaryData.paymentsPastDue,
          upcomingInvoices:
            response?.upcomingInvoices ?? defaultSummaryData.upcomingInvoices,
          paymentsNotReceived:
            response?.paymentsNotReceived ??
            defaultSummaryData.paymentsNotReceived,
        });
      }
    } catch (err) {
      // Don't set error if request was aborted (expected cancellation)
      if (signal.aborted) return;

      setError(
        err instanceof Error
          ? err
          : new Error('Failed to fetch invoices summary'),
      );
      setInvoiceSummary(defaultSummaryData);
    } finally {
      if (!signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [enabled]);

  // Refetch when enabled changes OR when company changes
  // Wait for company to initialize to avoid double fetching on page load
  // Note: We don't need debtorId in dependencies because when debtor changes,
  // company always changes too (gets reset then set to new value)
  useEffect(() => {
    if (enabled && companyInitialized && selectedCompanyId !== null) {
      // Create AbortController to cancel previous request when new one starts
      const abortController = new AbortController();

      fetchInvoicesSummary(abortController.signal);

      // Cleanup: abort the request if component unmounts or dependencies change
      return () => {
        abortController.abort();
      };
    }
  }, [enabled, fetchInvoicesSummary, selectedCompanyId, companyInitialized]);

  // Wrapper for manual refetch that doesn't require signal
  const refetch = useCallback(async () => {
    const abortController = new AbortController();
    await fetchInvoicesSummary(abortController.signal);
  }, [fetchInvoicesSummary]);

  return {
    invoiceSummary,
    isLoading: enabled ? isLoading : true,
    error,
    refetch,
  };
};